import React, { Fragment } from "react";
import Select from "react-select";
import { NumericFormat } from "react-number-format";
import { TrashIcon } from "@heroicons/react/outline";
import ErrorSelect from "../../Components/ErrorSelect";
import FormLabel from "../../Components/FormLabel";
import { classNames } from "../../Utils";
import { useTranslation } from "react-i18next";

const JournalEntryTable = ({
  accountItems,
  setaccountItems,
  accounts,
  debitError,
  allAccountLoading,
  inputErrors,
  isFormSubmit,
  updateAccountItem,
  handleRemoveRow,
  handleAddRow,
  selectedBusiness,
  businessIdParam,
  accountingDispatch,
  requestListAccount,
  accountItemsError,
  editModal = false,
}) => {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col sm:col-span-2">
      <div className="overflow-x-auto -mx-4 sm:-mx-6 lg:-mx-8">
        <div className="inline-block min-w-full align-middle md:px-6 lg:px-8">
          <div className="overflow-hidden border border-gray-200 rounded-lg shadow-sm">
            <table className="min-w-full divide-y divide-gray-300">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                    <FormLabel
                      htmlFor="account"
                      label={t("Account")}
                      required
                    />
                  </th>
                  <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                    <label
                      htmlFor="debit"
                      className="block text-sm font-medium text-gray-700"
                    >
                      {t("Debit")}
                    </label>
                  </th>
                  <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                    <label
                      htmlFor="credit"
                      className="block text-sm font-medium text-gray-700"
                    >
                      {t("Credit")}
                    </label>
                  </th>
                  <th></th>
                </tr>
              </thead>
              <tbody className="bg-white">
                {accountItems?.map((item, index) => {
                  const lastIndex = accountItems.length - 1;
                  const isAccountValid = item.account?.value;
                  const isMinimumAccountValid = accountItems.length >= 2;
                  const isDebitValid = item.debit.floatValue !== undefined;
                  const isCreditValid = item.credit.floatValue !== undefined;
                  const isRowValid =
                    (item.debit.floatValue !== 0 &&
                      item.credit.floatValue === 0) ||
                    (item.debit.floatValue === 0 &&
                      item.credit.floatValue !== 0);
                  const rowError =
                    !isAccountValid ||
                    !isDebitValid ||
                    !isCreditValid ||
                    !isRowValid;
                  const inputErrorKey = Object.keys(inputErrors || {});

                  return (
                    <Fragment key={index}>
                      <tr
                        className={classNames(
                          inputErrorKey.length > 0 && rowError
                            ? "border-none"
                            : "border-b border-gray-200"
                        )}
                        key={index}
                      >
                        <td className="px-4 py-2 font-medium text-sm text-gray-00 whitespace-nowrap min-w-[18rem]">
                          {debitError ? (
                            <ErrorSelect
                              name="account"
                              customClassNames="mt-0"
                              error={debitError}
                              onRefresh={() => {
                                if (businessIdParam || selectedBusiness.id) {
                                  requestListAccount(accountingDispatch, {
                                    businessID:
                                      selectedBusiness.id || businessIdParam,
                                    loadAll: true,
                                  });
                                }
                              }}
                            />
                          ) : (
                            <Select
                              id="account"
                              className={classNames(
                                !isAccountValid && isFormSubmit.current
                                  ? "border-red-500 rounded border focus:border-red-500 focus:ring-red-500"
                                  : "border-gray-300 focus:border-blue-500 focus:ring-blue-500",
                                "text-sm"
                              )}
                              classNamePrefix="select2-selection"
                              options={accounts.slice().map((obj) => ({
                                ...(editModal && obj),
                                value: obj.id,
                                label: `${obj.code} - ${obj.name}`,
                              }))}
                              value={item?.account || ""}
                              onChange={(selectedOption) =>
                                setaccountItems(
                                  updateAccountItem(index, {
                                    account: selectedOption,
                                  })
                                )
                              }
                              placeholder={t("Select Account")}
                              maxMenuHeight={150}
                              styles={{
                                menuPortal: (base) => ({
                                  ...base,
                                  zIndex: 9999,
                                }),
                              }}
                              menuPortalTarget={
                                !editModal ? document.body : null
                              }
                              isDisabled={allAccountLoading}
                              isLoading={allAccountLoading}
                            />
                          )}
                        </td>
                        <td className="px-4 py-2 text-sm text-left text-gray-700 whitespace-nowrap min-w-[10rem]">
                          <NumericFormat
                            type="text"
                            name="debit"
                            id="debit"
                            value={item.debit?.value || ""}
                            placeholder={t("Enter Amount")}
                            thousandSeparator="."
                            decimalSeparator=","
                            displayType="input"
                            prefix={"Rp"}
                            onValueChange={(values) => {
                              setaccountItems(
                                updateAccountItem(index, { debit: values })
                              );
                            }}
                            className={classNames(
                              !isDebitValid
                                ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                                : "border-gray-300 focus:border-blue-500 focus:ring-blue-500",
                              "block w-full text-sm rounded-md shadow-sm"
                            )}
                          />
                        </td>
                        <td className="px-4 py-2 text-sm text-left text-gray-700 whitespace-nowrap min-w-[10rem]">
                          <NumericFormat
                            type="text"
                            name="credit"
                            id="credit"
                            value={item.credit?.value || ""}
                            placeholder={t("Enter Amount")}
                            thousandSeparator="."
                            decimalSeparator=","
                            displayType="input"
                            prefix={"Rp"}
                            onValueChange={(values) => {
                              setaccountItems(
                                updateAccountItem(index, { credit: values })
                              );
                            }}
                            className={classNames(
                              !isCreditValid
                                ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                                : "border-gray-300 focus:border-blue-500 focus:ring-blue-500",
                              "block w-full text-sm rounded-md shadow-sm"
                            )}
                          />
                        </td>
                        <td className="px-4 py-2 text-sm text-left text-gray-700 whitespace-nowrap">
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              handleRemoveRow(index);
                            }}
                            className="flex items-center text-sm rounded-md shadow-sm border border-gray-300 px-4 py-2"
                            disabled={accountItems.length === 1}
                          >
                            <TrashIcon
                              className={`h-5 w-5 text-${
                                accountItems.length === 1
                                  ? "gray-400 cursor-not-allowed"
                                  : "red-500 cursor-pointer"
                              } `}
                            />
                          </button>
                        </td>
                      </tr>
                      {inputErrors?.debitCredit && !isRowValid && (
                        <tr
                          className={classNames(
                            inputErrorKey.length > 1 &&
                              (!isAccountValid ||
                                !isDebitValid ||
                                !isCreditValid)
                              ? "border-none"
                              : "border-b border-gray-200"
                          )}
                        >
                          <td
                            colSpan="7"
                            className="px-4 pb-2 text-sm text-left text-red-600"
                          >
                            <div>{t(inputErrors.debitCredit)}</div>
                          </td>
                        </tr>
                      )}
                      {inputErrors?.accountItems && !isAccountValid && (
                        <tr
                          className={classNames(
                            inputErrorKey.length > 1 &&
                              (!isDebitValid || !isCreditValid)
                              ? "border-none"
                              : "border-b border-gray-200"
                          )}
                        >
                          <td
                            colSpan="7"
                            className="px-4 pb-2 text-sm text-left text-red-600"
                          >
                            <div>{t(inputErrors.accountItems)}</div>
                          </td>
                        </tr>
                      )}
                      {inputErrors?.debitAmount && !isDebitValid && (
                        <tr
                          className={classNames(
                            inputErrorKey.length > 1 && !isCreditValid
                              ? "border-none"
                              : "border-b border-gray-200"
                          )}
                        >
                          <td
                            colSpan="7"
                            className="px-4 pb-2 text-sm text-left text-red-600"
                          >
                            <div>{t(inputErrors.debitAmount)}</div>
                          </td>
                        </tr>
                      )}
                      {inputErrors?.creditAmount && !isCreditValid && (
                        <tr className="border-b border-gray-200">
                          <td
                            colSpan="7"
                            className="px-4 pb-2 text-sm text-left text-red-600"
                          >
                            <div>{t(inputErrors.creditAmount)}</div>
                          </td>
                        </tr>
                      )}
                      {!isMinimumAccountValid &&
                        isFormSubmit.current &&
                        index === lastIndex && (
                          <tr className="border-b border-gray-200">
                            <td
                              colSpan="7"
                              className="px-4 py-2 text-sm text-left text-red-600"
                            >
                              <div>{t("Minimum 2 journal entry.")}</div>
                            </td>
                          </tr>
                        )}
                      {accountItemsError && index === lastIndex && (
                        <tr className="border-b border-gray-200">
                          <td
                            colSpan="7"
                            className="px-4 py-2 text-sm text-left text-red-600"
                          >
                            <div>{t(accountItemsError)}</div>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
                <tr>
                  <td
                    className="p-4 text-sm text-left text-gray-700 whitespace-nowrap"
                    colSpan={4}
                  >
                    <div className="flex gap-3">
                      <button
                        type="button"
                        onClick={handleAddRow}
                        className="text-sm font-medium text-blue-600"
                      >
                        {t("Add Journal Entry")}
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JournalEntryTable;
